/**
 * Local-first storage.
 *
 * Every log action is written here BEFORE any network call, and the UI renders from
 * here. That is the entire product: the checkmark appears the instant Richard taps,
 * whether or not there is signal in the basement. Everything else is decoration.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { LogEventInput, MedicationVersion } from '@rx/shared';

export interface OutboxEntry {
  eventId: string;
  patientId: string;
  event: LogEventInput;
  queuedAt: string;
  attempts: number;
  lastError?: string;
}

export interface CachedRegimen {
  patientId: string;
  tz: string;
  dayStartHour: number;
  /** Cached too: the test banner must appear offline as well as online. */
  isTest?: boolean;
  pharmacy?: { name?: string; phone?: string; address?: string };
  medications: MedicationVersion[];
  fetchedAt: string;
}

/** Events confirmed by the server, cached so the day renders offline. */
export interface CachedEvent {
  key: string;
  patientId: string;
  logicalDate: string;
  event: Record<string, unknown>;
}

interface RxSchema extends DBSchema {
  outbox: { key: string; value: OutboxEntry; indexes: { byPatient: string } };
  regimen: { key: string; value: CachedRegimen };
  events: { key: string; value: CachedEvent; indexes: { byDay: [string, string] } };
  auth: { key: string; value: unknown };
  meta: { key: string; value: unknown };
}

let dbPromise: Promise<IDBPDatabase<RxSchema>> | undefined;

export function db(): Promise<IDBPDatabase<RxSchema>> {
  dbPromise ??= openDB<RxSchema>('rx-logger', 1, {
    upgrade(database) {
      const outbox = database.createObjectStore('outbox', { keyPath: 'eventId' });
      outbox.createIndex('byPatient', 'patientId');
      database.createObjectStore('regimen', { keyPath: 'patientId' });
      const events = database.createObjectStore('events', { keyPath: 'key' });
      events.createIndex('byDay', ['patientId', 'logicalDate']);
      database.createObjectStore('auth');
      database.createObjectStore('meta');
    },
  });
  return dbPromise;
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

export async function enqueue(entry: OutboxEntry): Promise<void> {
  await (await db()).put('outbox', entry);
}

export async function outboxEntries(): Promise<OutboxEntry[]> {
  const all = await (await db()).getAll('outbox');
  // UUIDv7 sorts chronologically, so the backlog replays in the order it happened.
  return all.sort((a, b) => a.eventId.localeCompare(b.eventId));
}

export async function outboxCount(): Promise<number> {
  return (await db()).count('outbox');
}

/** Removed only after the server has acknowledged the id. */
export async function dequeue(eventIds: readonly string[]): Promise<void> {
  const database = await db();
  const tx = database.transaction('outbox', 'readwrite');
  await Promise.all(eventIds.map((id) => tx.store.delete(id)));
  await tx.done;
}

export async function markAttempt(eventId: string, error: string): Promise<void> {
  const database = await db();
  const entry = await database.get('outbox', eventId);
  if (!entry) return;
  await database.put('outbox', { ...entry, attempts: entry.attempts + 1, lastError: error });
}

// ---------------------------------------------------------------------------
// Cached reads
// ---------------------------------------------------------------------------

export async function putRegimen(regimen: CachedRegimen): Promise<void> {
  await (await db()).put('regimen', regimen);
}

export async function getRegimen(patientId: string): Promise<CachedRegimen | undefined> {
  return (await db()).get('regimen', patientId);
}

export async function putEvents(
  patientId: string,
  logicalDate: string,
  events: readonly Record<string, unknown>[],
): Promise<void> {
  const database = await db();
  const tx = database.transaction('events', 'readwrite');
  for (const event of events) {
    const rawId: unknown = event.eventId;
    if (typeof rawId !== 'string' || rawId.length === 0) continue;
    const eventId = rawId;
    await tx.store.put({ key: `${patientId}|${eventId}`, patientId, logicalDate, event });
  }
  await tx.done;
}

export async function getDayEvents(
  patientId: string,
  logicalDate: string,
): Promise<Record<string, unknown>[]> {
  const rows = await (await db()).getAllFromIndex('events', 'byDay', [patientId, logicalDate]);
  return rows.map((r) => r.event);
}

// ---------------------------------------------------------------------------
// Small key/value corners
// ---------------------------------------------------------------------------

export async function setAuth(key: string, value: unknown): Promise<void> {
  await (await db()).put('auth', value, key);
}

export async function getAuth<T>(key: string): Promise<T | undefined> {
  return (await db()).get('auth', key) as Promise<T | undefined>;
}

export async function clearAuth(): Promise<void> {
  await (await db()).clear('auth');
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', value, key);
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db()).get('meta', key) as Promise<T | undefined>;
}

/**
 * Ask the browser to keep this origin's storage.
 *
 * iOS evicts non-persisted origin storage after roughly seven days of non-use, which
 * is exactly how a week of medication history would silently disappear.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    // Typed as always present; absent in several real browsers, and it throws outright
    // in some privacy modes — hence both the guard and the catch.
    const storage = (navigator as unknown as { storage?: StorageManager }).storage;
    if (!storage?.persist) return false;
    if (await storage.persisted()) return true;
    return await storage.persist();
  } catch {
    return false;
  }
}
